import { motion } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBtaW5pbWFsaXN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3ODI4OTAxNjV8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.08, duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden pt-32 md:pt-40"
    >
      <div className="absolute inset-0 -z-10">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black" />
      </div>

      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-8">
        <motion.div
          initial="hidden"
          animate="visible"
          className="col-span-full md:col-span-11"
        >
          <motion.div variants={fadeUp} custom={0} className="flex items-center gap-2 mb-8">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-3 py-1 text-xs font-mono uppercase tracking-[0.2em] text-zinc-300">
              <Sparkles size={12} className="text-indigo-400" />
              Booking Q1 2026 — 2 slots
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={1}
            data-testid="hero-heading"
            className="font-display text-4xl sm:text-5xl lg:text-[5.5rem] font-black tracking-tighter leading-[0.95] text-white text-balance"
          >
            Design that makes
            <br />
            your product feel
            <br />
            <span className="italic font-serif text-zinc-100" style={{ fontFamily: "'Outfit', serif", fontStyle: "italic" }}>
              inevitable
            </span>
            <span className="text-indigo-400">.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-8 max-w-2xl text-base lg:text-lg text-zinc-400 leading-relaxed"
          >
            HeroWebDesign is a one-person studio for founders who care about the details.
            Websites, product UI and brand identity — shipped in weeks, not quarters.
          </motion.p>

          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a
              href="#estimator"
              data-testid="hero-estimator-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-all active:scale-[0.98]"
            >
              <Sparkles size={16} />
              Get an instant estimate
            </a>
            <a
              href="#estimator"
              data-testid="hero-work-cta"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 backdrop-blur text-white px-6 py-3.5 text-sm font-medium hover:bg-white/10 transition-all active:scale-[0.98]"
            >
              See how it works
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={4}
            className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 max-w-3xl"
          >
            {[
              { k: "2", v: "Years building" },
              { k: "20+", v: "Sites shipped" },
              { k: "<24h", v: "Reply time" },
              { k: "$0", v: "Discovery call" },
            ].map((s) => (
              <div key={s.v} data-testid={`hero-stat-${s.v.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="font-display text-3xl md:text-4xl font-bold text-white">{s.k}</div>
                <div className="mt-1 text-xs font-mono uppercase tracking-widest text-zinc-500">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <a
        href="#services"
        aria-label="Scroll to services"
        className="hidden md:flex absolute bottom-10 left-1/2 -translate-x-1/2 items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white transition"
      >
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </a>
    </section>
  );
}
